import React, { useEffect, useState, useMemo } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { array, arrayOf, bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import get from 'lodash/get';
import { calculateDrivingExperience } from '../../util/drivingLicenseUtils';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import config from '../../config';
import { Button, IconSpinner, ModalInMobile, NamedLink, PrimaryButton } from '../../components';
import { BookingTimeForm } from '../../forms';
import moment from 'moment';
import css from './BookingPanel.css';
import { types } from '../../util/sdkLoader';
import { getDatesFromUrlParams, momentTimeOfDayFromLocalToTimeZone } from '../../util/dates';

const { Money } = types;

const TODAY = new Date();
const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;

const priceData = (price, intl, longTermRental) => {
  if (price && price.currency === config.currency) {
    const priceMaybe = longTermRental ? new Money(price.amount * 30, price.currency) : price;
    const formattedPrice = formatMoney(intl, priceMaybe);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openBookModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), book: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeBookModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'book');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    title,
    subTitle,
    authorDisplayName,
    onManageDisableScrolling,
    timeSlots,
    history,
    location,
    intl,
    search,
    currentUser,
    initialDate,
    onCheckingVoucher,
    checkedCode,
    checkCodeInProgress,
    checkCodeErorr,
    onResetCode,
    calculateMobileHeight,
    isLiveListing,
    onReadInsurance,
    requestButtonId,
    onFetchTimeSlots,
    onEstimateBreakdown,
    tripFeesExperiment,
    estimateBreakdownInProgress,
    estimatedTx,
    onOpenRentalAgreement,
    bookingConfig,
    estimateError,
    fetchTimeSlotsInProgress,
    localTimeZone,
    isLongTerm,
    isLongTermBooking,
    shouldShowPricePerMonth,
    formattedPrice,
    priceTitle,
    longTermRental,
    monthlyTimeSlots,
    transaction,
    updateBooking,
    onRequestToUpdateBooking,
    getChildTransactionData,
    isEditTrip,
    gtmEvents,
    searchQueryData
  } = props;
  const { price } = listing.attributes;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm =
    hasListingState && !isClosed && isLiveListing && !fetchTimeSlotsInProgress;
  const showClosedListingHelpText = listing.id && isClosed;
  // const { formattedPrice, priceTitle } = priceData(price, intl, shouldShowPricePerMonth);
  const isBook = !!parse(location.search).book;
  const timeZone =
    listing.attributes.availabilityPlan && listing.attributes.availabilityPlan.timezone;

  const isInstantBooking = listing && listing.attributes.publicData.instantBooking;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const bookingPanelMobileElem = window.document.getElementsByClassName(
        css.openBookingFormWrapper
      );
      const bookingPanelMobileHeight =
        bookingPanelMobileElem &&
        bookingPanelMobileElem[0] &&
        bookingPanelMobileElem[0].clientHeight;
      calculateMobileHeight(bookingPanelMobileHeight);
    }
  }, []);

  const subTitleText = !!subTitle
    ? subTitle
    : showClosedListingHelpText
    ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
    : null;

  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey =
    longTermRental && shouldShowPricePerMonth ? 'ListingPage.perMonth' : 'ListingPage.perDay';

  const classes = updateBooking
    ? classNames(rootClassName || css.updateBookingPanel)
    : classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.bookingTitle);

  const listingParams = {
    slug: createSlug(listing && listing.attributes.title),
    id: listing && listing.id.uuid,
  };

  const isPCar =
    false &&
    listing &&
    listing.id &&
    listing.attributes.publicData &&
    listing.attributes.publicData.vehicleType &&
    listing.attributes.publicData.vehicleType.match(/^P/);
  // todo: base on vehicle type, filter the timeslot

  const { startDate, endDate, pickupTime, dropoffTime } = initialDate;
  const bookingStartDateMaybe = startDate ? startDate : null;
  const bookingEndDateMaybe = endDate ? endDate : null;
  const pickupTimeToDate = pickupTime ? moment(pickupTime, 'hh:mm a').toDate() : null;

  const dropoffTimeToDate = dropoffTime ? moment(dropoffTime, 'hh:mm a').toDate() : null;
  let bookingStartTimeMaybe = null;
  let bookingEndTimeMaybe = null;
  if (pickupTimeToDate && bookingStartDateMaybe) {
    bookingStartTimeMaybe = bookingStartDateMaybe.setHours(
      pickupTimeToDate.getHours(),
      pickupTimeToDate.getMinutes()
    );
  }

  if (dropoffTimeToDate && bookingEndDateMaybe) {
    bookingEndTimeMaybe = bookingEndDateMaybe.setHours(
      dropoffTimeToDate.getHours(),
      dropoffTimeToDate.getMinutes()
    );
  }

  const initialValues = {
    bookingStartDate: bookingStartDateMaybe ? { date: bookingStartDateMaybe } : null,
    bookingEndDate: bookingEndDateMaybe ? { date: bookingEndDateMaybe } : null,
    bookingStartTime: bookingStartTimeMaybe
      ? momentTimeOfDayFromLocalToTimeZone(bookingStartTimeMaybe, localTimeZone).format('x')
      : null,
    bookingEndTime: bookingEndTimeMaybe
      ? momentTimeOfDayFromLocalToTimeZone(bookingEndTimeMaybe, localTimeZone).format('x')
      : null,
  };

  const _handleLocationStates = locationParams => {
    if (
      location.state &&
      location.state.startDate &&
      location.state.startDate &&
      !moment(location.state.startDate).isValid() &&
      !moment(location.state.endDate).isValid()
    ) {
      const queryMaybe = parse(location.search);
      const resp = getDatesFromUrlParams(queryMaybe.dates, queryMaybe.hours);
      let passLocationStates = locationParams.state;
      passLocationStates = {
        ...passLocationStates,
        startDate: resp.start instanceof Date ? resp.start : resp.start.toDate(),
        endDate: resp.end instanceof Date ? resp.end : resp.end.toDate(),
        params: listingParams,
        isFromListingPage: true,
      };
      return passLocationStates;
    } else {
      return {
        ...locationParams.state,
        params: listingParams,
        isFromListingPage: true,
      };
    }
  };

  const isNewCar = get(listing, 'attributes.metadata.isNewCar');

  // Calculating the users age
  const userAge = () => {
    let age = get(currentUser, "attributes.profile.protectedData.age", null) || get(currentUser, "attributes.profile.metadata.intercomUserStat.age", null);
    if (age) return age;
    const dob = get(currentUser, "attributes.profile.protectedData.dateOfBirth", null);
    if (dob) {
      age = moment().diff(moment(dob), 'years');
      return age;
    }
  }

  // Setting flag for cdg and komoco cars.
  const cdgKomocoCars = get(listing, "attributes.publicData.isYoungDriverNotAllowed", null);

  // Setting flag for HPV (High Performance Vehicle) cars.
  const isHpvCar = get(listing, "attributes.publicData.isHpvCar", null);

  // Memoize expensive calculations - only recalculate when currentUser changes
  // Calculate driving experience (handles both new and old user structures)
  // Uses calculateDrivingExperience utility function
  // Default behavior includes foreign license data (for young driver checks - CDG/Komoco)
  const drivingExperience = useMemo(() => {
    return currentUser ? calculateDrivingExperience(currentUser) : 0;
  }, [currentUser]);
  
  // For HPV restrictions: exclude foreign license data (Singapore only)
  const drivingExperienceForHpv = useMemo(() => {
    return currentUser ? calculateDrivingExperience(currentUser, false) : 0;
  }, [currentUser]);

  // Memoize age calculation
  const userAgeValue = useMemo(() => {
    let age = get(currentUser, "attributes.profile.protectedData.age", null) || get(currentUser, "attributes.profile.metadata.intercomUserStat.age", null);
    if (age) return age;
    const dob = get(currentUser, "attributes.profile.protectedData.dateOfBirth", null);
    if (dob) {
      age = moment().diff(moment(dob), 'years');
      return age;
    }
    return null;
  }, [currentUser]);

  // Setting a flag if user is allowed to book that certain car or not.
  // Memoized to avoid recalculating on every render
  const isAllowedToBook = useMemo(() => {
    // Early return if no restrictions
    if (!cdgKomocoCars && !isHpvCar) {
      return true;
    }

    // Check CDG/Komoco restrictions (age >= 22, experience >= 2 years)
    // Uses default behavior - includes foreign license data (existing behavior)
    if (cdgKomocoCars) {
      const age = userAgeValue;
      if (!(age >= 22 && drivingExperience >= 2)) {
        return false;
      }
    }

    // Check HPV restrictions (age 30-65, experience >= 5 years)
    // Excludes foreign license data (Singapore only)
    if (isHpvCar) {
      const age = userAgeValue;
      if (!(age >= 30 && age <= 65 && drivingExperienceForHpv >= 5)) {
        return false;
      }
    }

    return true;
  }, [cdgKomocoCars, isHpvCar, userAgeValue, drivingExperience, drivingExperienceForHpv]);

  // Price display logic for AU-style layout
  const listingPrice = get(listing, 'attributes.price', '');
  const hourlyPrice = get(listing, 'attributes.publicData.pricing.hourlyRegularPrice', '');
  
  // Track differenceInHours from form
  const [differenceInHours, setDifferenceInHoursState] = useState(null);
  
  // Calculate diffHours for hourly booking detection
  let diffHours = 0;
  let hourlyPriceSeen = false;
  
  if (differenceInHours && differenceInHours > 0) {
    diffHours = differenceInHours;
  } else {
    if (bookingStartTimeMaybe && bookingEndTimeMaybe) {
      const startMoment = moment(bookingStartTimeMaybe);
      const endMoment = moment(bookingEndTimeMaybe);
      diffHours = endMoment.diff(startMoment, 'hours', true);
    }
  }
  
  // Calculate if this is an hourly booking (6 hours or less)
  if (diffHours && diffHours <= 6 && hourlyPrice) {
    hourlyPriceSeen = true;
  }
  
  const formattedListingPrice = listingPrice && formatMoney(intl, listingPrice, 1);
  
  // Check if dates AND times are selected
  const hasSelectedDates = bookingStartDateMaybe && bookingEndDateMaybe;
  const hasSelectedTimes = bookingStartTimeMaybe && bookingEndTimeMaybe;
  const hasSelectedDatesAndTimesFromInitial = hasSelectedDates && hasSelectedTimes;
  
  // Check if dates/times are selected dynamically (from form)
  const hasSelectedDatesAndTimesDynamically = estimatedTx && estimatedTx.attributes && estimatedTx.attributes.payinTotal;
  const hasSelectedDatesAndTimes = hasSelectedDatesAndTimesFromInitial || hasSelectedDatesAndTimesDynamically;
  
  // Calculate total trip price to display above title
  const totalTripPrice = hasSelectedDatesAndTimes && estimatedTx && estimatedTx.attributes && estimatedTx.attributes.payinTotal
    ? formatMoney(intl, estimatedTx.attributes.payinTotal, 2)
    : formattedListingPrice;
  
  // Determine price label text and whether to show /day
  let priceLabelText = '';
  let showPerDay = false;
  
  if (!hasSelectedDatesAndTimes || !estimatedTx) {
    priceLabelText = 'Listing price';
    showPerDay = true;
  } else if (hourlyPriceSeen) {
    priceLabelText = 'Total duration charge';
    showPerDay = false;
  } else {
    priceLabelText = 'Total trip price';
    showPerDay = false;
  }
  
  // Display price
  const displayPrice = totalTripPrice || formattedListingPrice;
  
  // Handler to receive differenceInHours from form
  const handleDifferenceInHours = (hours) => {
    setDifferenceInHoursState(hours);
  };

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="BookingDatesFormInModal"
        isModalOpenOnMobile={isBook}
        onClose={() => closeBookModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={' '}
      >
        <div className={css.modalHeading}>
          <h1 className={css.title}>
            {listing && listing.attributes && listing.attributes.title
              ? `Book ${listing.attributes.title}`
              : title}
          </h1>
        </div>

        {!updateBooking && (
          <div className={css.bookingHeadingMobile}>
            <h2 className={titleClasses}>{title}</h2>
          </div>
        )}

        {!updateBooking && (
          <div className={css.bookingHeading}>
            {hasSelectedDatesAndTimes && estimateBreakdownInProgress ? (
              <div className={css.priceContainer}>
                <div className={css.priceLoadingContainer}>
                  <IconSpinner />
                  <span className={css.priceLoadingText}>Calculating price...</span>
                </div>
              </div>
            ) : displayPrice ? (
              <div className={css.priceContainer}>
                <div className={css.totalTripPrice}>
                  <span className={css.priceWithPerDay}>
                    <span className={css.priceValue}>{displayPrice}</span>
                    {showPerDay && <span className={css.pricePerDay}>/day</span>}
                  </span>
                  {priceLabelText && (
                    <div className={css.priceLabelText}>
                      {priceLabelText}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            <h2 className={titleClasses}>{title}</h2>
          </div>
        )}
        {updateBooking && (
          <div>
            <h3 className={css.updateBookingTitle}>
              <FormattedMessage id="BookingPanel.updateBookingTitle" />
            </h3>
            <p className={css.updateBookingSubTitle}>
              <FormattedMessage id="BookingPanel.updateBookingSubTitle" />
            </p>
            <p className={css.updateBookingSubTitle}>
              <FormattedMessage id="BookingPanel.updateBookingSubTitle2" />
            </p>
          </div>
        )}
        {showBookingDatesForm && !updateBooking ? (
          <BookingTimeForm
            currentUser={currentUser}
            timeSlots={timeSlots}
            monthlyTimeSlots={monthlyTimeSlots}
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.submitButtonWrapper}
            unitType={unitType}
            onSubmit={onSubmit}
            price={price}
            searchQueryData={searchQueryData}
            isOwnListing={isOwnListing}
            listingId={listing.id}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            listing={listing}
            onResetCode={onResetCode}
            checkedCode={checkedCode}
            checkCodeErorr={checkCodeErorr}
            checkCodeInProgress={checkCodeInProgress}
            onCheckingVoucher={onCheckingVoucher}
            onEstimateBreakdown={onEstimateBreakdown}
            tripFeesExperiment={tripFeesExperiment}
            estimateBreakdownInProgress={estimateBreakdownInProgress}
            estimatedTx={estimatedTx}
            requestButtonId={requestButtonId}
            listingParams={listingParams}
            onManageDisableScrolling={onManageDisableScrolling}
            onReadInsurance={onReadInsurance}
            onOpenRentalAgreement={onOpenRentalAgreement}
            bookingConfig={bookingConfig}
            initialDate={initialDate}
            estimateError={estimateError}
            initialValues={initialValues}
            localTimeZone={localTimeZone}
            isNewCar={isNewCar}
            isLongTerm={isLongTerm}
            isLongTermBooking={isLongTermBooking || shouldShowPricePerMonth}
            transaction={transaction}
            updateBooking={updateBooking}
            onRequestToUpdateBooking={onRequestToUpdateBooking}
            getChildTransactionData={getChildTransactionData}
            location={location}
            gtmEvents={gtmEvents}
            isAllowedToBook={isAllowedToBook}
          />
        ) : fetchTimeSlotsInProgress ? (
          <div className={css.loadingTimeSlotWrapper}>
            <IconSpinner />
          </div>
        ) : null}
      </ModalInMobile>
      {showBookingDatesForm && updateBooking ? (
        <BookingTimeForm
          currentUser={currentUser}
          timeSlots={timeSlots}
          monthlyTimeSlots={monthlyTimeSlots}
          className={css.bookingForm}
          formId="BookingPanel"
          submitButtonWrapperClassName={css.submitButtonWrapper}
          unitType={unitType}
          onSubmit={onSubmit}
          price={price}
          isOwnListing={isOwnListing}
          listingId={listing.id}
          onFetchTimeSlots={onFetchTimeSlots}
          startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
          timeZone={timeZone}
          listing={listing}
          onResetCode={onResetCode}
          checkedCode={checkedCode}
          checkCodeErorr={checkCodeErorr}
          checkCodeInProgress={checkCodeInProgress}
          onCheckingVoucher={onCheckingVoucher}
          onEstimateBreakdown={onEstimateBreakdown}
          tripFeesExperiment={tripFeesExperiment}
          estimateBreakdownInProgress={estimateBreakdownInProgress}
          estimatedTx={estimatedTx}
          requestButtonId={requestButtonId}
          listingParams={listingParams}
          onManageDisableScrolling={onManageDisableScrolling}
          onReadInsurance={onReadInsurance}
          onOpenRentalAgreement={onOpenRentalAgreement}
          bookingConfig={bookingConfig}
          initialDate={initialDate}
          estimateError={estimateError}
          initialValues={initialValues}
          localTimeZone={localTimeZone}
          isNewCar={isNewCar}
          isLongTerm={isLongTerm}
          isLongTermBooking={isLongTermBooking || shouldShowPricePerMonth}
          transaction={transaction}
          updateBooking={updateBooking}
          onRequestToUpdateBooking={onRequestToUpdateBooking}
          getChildTransactionData={getChildTransactionData}
          location={location}
          gtmEvents={gtmEvents}
          isAllowedToBook={isAllowedToBook}
        />
      ) : fetchTimeSlotsInProgress ? (
        <div className={css.loadingTimeSlotWrapper}>
          <IconSpinner />
        </div>
      ) : null}
      {!updateBooking && (
        <div className={css.openBookingFormWrapper}>
          <div className={css.openBookingForm}>
            {isAllowedToBook && (
              hasSelectedDatesAndTimes && estimateBreakdownInProgress ? (
                <div className={css.priceWrapper}>
                  <div className={css.priceLoadingContainer}>
                    <IconSpinner />
                    <span className={css.priceLoadingText}>Calculating...</span>
                  </div>
                </div>
              ) : displayPrice ? (
                <div className={css.priceWrapper}>
                  <div className={css.totalTripPrice}>
                    <span className={css.priceWithPerDay}>
                      <span className={css.priceValue}>{displayPrice}</span>
                      {showPerDay && <span className={css.pricePerDay}>/day</span>}
                    </span>
                  </div>
                  {priceLabelText && (
                    <div className={css.priceLabelText}>
                      {priceLabelText}
                    </div>
                  )}
                </div>
              ) : null
            )}

            {isLiveListing ? (
              <div>
                {isAllowedToBook ? (
                <p className={css.smallPrintForMember}>
                  {/* <FormattedMessage id="BookingPanel.memberUseDrivelah" /> */}
                </p>
                ) : (
                <p className={css.error} style={{paddingTop:"5px"}}>
                  <span style={{marginRight:"5px"}}>
                        <svg id="Group_5243" data-name="Group 5243" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 22 22">
                          <g id="Ellipse_1207" data-name="Ellipse 1207" transform="translate(0 0)" fill="none" stroke="red" stroke-width="2">
                            <circle cx="11" cy="11" r="11" stroke="none" />
                            <circle cx="11" cy="11" r="10" fill="none" />
                          </g>
                          <path id="Path_4525" data-name="Path 4525" d="M1.712-3.168a.689.689,0,0,1-.776-.748L.77-9.234a.659.659,0,0,1,.748-.776H2.46a.659.659,0,0,1,.748.776L3.041-3.916a.689.689,0,0,1-.776.748ZM.895-1.423a.673.673,0,0,1,.762-.762h.665a.673.673,0,0,1,.762.762v.554a.673.673,0,0,1-.762.762H1.656A.673.673,0,0,1,.895-.869Z" transform="translate(9.01 16.059)" fill="red" />
                        </svg>
                  </span>
                  <FormattedMessage id={isHpvCar ? "BookingPanel.notEligibleHpv" : "BookingPanel.notEligibleYoungDriver"} />
                </p>
                )}

                {currentUser && currentUser.id ? (
                  <Button
                    rootClassName={css.bookButton}
                    disabled={!currentUser.canRequestBooking || !isAllowedToBook}
                    onClick={() => {
                      openBookModal(isOwnListing, isClosed, history, location);
                    }}
                  >
                    <FormattedMessage id="BookingPanel.ctaButtonMessage" />
                  </Button>
                ) : (
                  <NamedLink
                    name="LoginPage"
                    to={{
                      state: _handleLocationStates(location),
                      // state: { ...location.state, params: listingParams, isFromListingPage: true },
                    }}
                  >
                    <PrimaryButton type="button">
                      <FormattedMessage id="BookingPanel.ctaButtonMessage" />
                    </PrimaryButton>
                  </NamedLink>
                )}

                {!isInstantBooking && (
                  <p className={css.smallPrint}>
                    {/* <FormattedMessage id="BookingPanel.noCharge" /> */}
                  </p>
                )}
              </div>
            ) : (
              <div className={css.closedListingButton}>
                <FormattedMessage id="ListingPage.listingNotLive" />
              </div>
            )}
            {isClosed ? (
              <div className={css.closedListingButton}>
                <FormattedMessage id="BookingPanel.closedListingButtonText" />
              </div>
            ) : null}
          </div>
          {isOwnListing && (
            <p className={css.smallPrint}>
              <FormattedMessage id="BookingPanel.ownListing" />
            </p>
          )}
        </div>
      )}
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  subTitle: null,
  unitType: config.bookingUnitType,
  timeSlots: null,
  fetchTimeSlotsError: null,
  isLiveListing: false,
  monthlyTimeSlots: null,
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.bookingUnitType,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  subTitle: oneOfType([node, string]),
  authorDisplayName: oneOfType([node, string]).isRequired,
  onManageDisableScrolling: func.isRequired,
  timeSlots: oneOfType([arrayOf(propTypes.timeSlot), array]),
  monthlyTimeSlots: object,
  fetchTimeSlotsError: propTypes.error,
  isLiveListing: bool,
  onReadInsurance: func,
  requestButtonId: string,
  tripFeesExperiment: string,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
  isLongTerm: bool,
  isLongTermBooking: bool,
};

export default compose(withRouter, injectIntl)(BookingPanel);
